import { articleApiClient } from '@src/api/tenant/article/articleApiClient';
import { Article, ArticleForm, ArticleFormError } from '@src/api/types/article';
import { MediaCategory } from '@src/api/types/mediaCategory';
import { MediaTag } from '@src/api/types/mediaTag';
import { FORM_CONSTANTS } from '@src/api/types/supportService';
import { useFileUpload } from '@src/hooks/useFileUpload';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { useTagManagement } from '@src/hooks/useTagManagement';
import { Switch } from '@src/ui/elements/form';
import FormField from '@src/ui/elements/form/FormField';
import ImageUploadField from '@src/ui/elements/form/ImageUploadField';
import Input from '@src/ui/elements/form/Input';
import InputCalendar from '@src/ui/elements/form/InputCalendar';
import PDFUploadField from '@src/ui/elements/form/PDFUploadField';
import Select from '@src/ui/elements/form/Select';
import TagField from '@src/ui/elements/form/TagField';
import Textarea from '@src/ui/elements/form/Textarea';
import React, { ChangeEvent, useCallback } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { RelatedArticles } from './relatedArticles';

type SettingFormProps = {
  categoryOptions: MediaCategory[];
  availableTags?: MediaTag[];
  setAvailableTags: (tags: MediaTag[]) => void;
  errors?: ArticleFormError;
  setErrors?: (errors: ArticleFormError) => void;
  methods: UseFormReturn<ArticleForm>;
};

const SettingForm: React.FC<SettingFormProps> = ({
  categoryOptions,
  availableTags = [],
  setAvailableTags,
  errors: serverErrors = {} as ArticleFormError,
  setErrors,
  methods,
}) => {
  const fieldRefs = useScrollToError(serverErrors);
  const [pdfUrl, originalPdfName] = useWatch({
    control: methods?.control,
    name: ['pdf_url', 'original_pdf_name'],
  });
  const onChangeHandler = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (name: keyof ArticleForm, value: any) => {
      methods.setValue(name, value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [methods],
  );

  const { handleUpload, handleDelete, isUploading, previewBase64 } = useFileUpload({
    onSuccess: (url) => onChangeHandler('thumbnail_url', url),
    onError: (error) => setErrors?.({ ...serverErrors, thumbnail_url: [error] }),
    apiClientUploadFile: articleApiClient.uploadThumbnail,
    apiClientDeleteFile: articleApiClient.deleteFile,
  });

  const {
    handleUpload: handlePdfUpload,
    handleDelete: handlePdfDelete,
    isUploading: isPdfUploading,
  } = useFileUpload({
    onSuccess: (url, originalFileName) => {
      onChangeHandler('pdf_url', url);
      onChangeHandler('original_pdf_name', originalFileName || '');
    },
    onError: (error) => setErrors?.({ ...serverErrors, pdf_url: [error] }),
    apiClientUploadFile: articleApiClient.uploadPdf,
    apiClientDeleteFile: articleApiClient.deleteFile,
  });

  const {
    newTagName,
    setNewTagName,
    handleNewTag,
    handleAddTag,
    handleRemoveTag,
    tagError,
    selectedTags,
  } = useTagManagement({
    initialTags: methods.getValues('media_tags') as number[],
    availableTags,
    setAvailableTags,
    onTagsChange: (tags) => onChangeHandler('media_tags', tags),
    type: 'media',
  });

  const isPickup = useWatch({ control: methods.control, name: 'is_pickup' });
  const isTopDisplay = useWatch({ control: methods.control, name: 'is_top_display' });
  const isInformation = useWatch({ control: methods.control, name: 'is_information' });

  return (
    <>
      <FormField
        control={methods.control}
        errors={serverErrors.title}
        name='title'
        label='記事タイトル'
        maxLength={FORM_CONSTANTS.MAX_TITLE_LENGTH}
        component={Input}
        placeholder='タイトル'
        componentProps={{
          onChange: (e: ChangeEvent<HTMLInputElement>) => onChangeHandler('title', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.title = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.published_at}
        name='published_at'
        label={
          <div className='flex items-end gap-[6px]'>
            <span>公開日</span>
            <span className='text-[10px]'>※09:00公開</span>
          </div>
        }
        component={InputCalendar}
        componentProps={{
          initialValue: methods.getValues('published_at'),
          onChange: (value: string | null) =>
            onChangeHandler('published_at', value === null ? '' : value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.published_at = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.media_category_id}
        name='media_category_id'
        label='カテゴリ'
        component={Select}
        componentProps={{
          items: categoryOptions?.map((category) => ({
            id: category.id.toString(),
            name: category.name,
          })),
          onSelect: (id: string) => onChangeHandler('media_category_id', id),
          initialValue: methods.getValues('category.id'),
        }}
        fieldRef={(el) => {
          fieldRefs.current.media_category_id = el;
        }}
      />

      <TagField
        newTagName={newTagName}
        setNewTagName={setNewTagName}
        tags={
          selectedTags.length > 0 ? selectedTags : (methods.getValues('media_tags') as number[])
        }
        onNewTag={handleNewTag}
        onAddTag={handleAddTag}
        onRemoveTag={handleRemoveTag}
        error={tagError}
        availableTags={availableTags}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.description}
        name='description'
        label='概要（description）'
        component={Textarea}
        placeholder='記事の概要を記入してください。検索結果やSNSでシェアしたときに Title とともに表示される概要文です。'
        maxLength={FORM_CONSTANTS.MAX_DESCRIPTION_LENGTH}
        componentProps={{
          onChange: (e: ChangeEvent<HTMLTextAreaElement>) =>
            onChangeHandler('description', e.target.value),
        }}
        fieldRef={(el) => {
          fieldRefs.current.description = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.related_articles}
        name='related_articles'
        component={RelatedArticles}
        componentProps={{
          setRelatedArticles: (article: Article[]) => onChangeHandler('related_articles', article),
          initialValue: methods.getValues('related_articles'),
          currentArticle: methods.getValues(),
        }}
        fieldRef={(el) => {
          fieldRefs.current.related_articles = el;
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.is_pickup}
        label='ピックアップ記事'
        labelPosition='left'
        name='is_pickup'
        component={Switch}
        componentProps={{
          onCheck: () => {
            methods.setValue('is_pickup', true, { shouldValidate: true, shouldDirty: true });
            methods.setValue('is_information', false, { shouldValidate: true, shouldDirty: true });
          },
          onUncheck: () => {
            methods.setValue('is_pickup', false, { shouldValidate: true, shouldDirty: true });
          },
          checked: isPickup,
        }}
        fieldRef={(el) => {
          if (el) {
            fieldRefs.current.is_pickup = el;
          }
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.is_top_display}
        label='上位表示'
        labelPosition='left'
        name='is_top_display'
        component={Switch}
        componentProps={{
          onCheck: () => {
            methods.setValue('is_top_display', true, { shouldValidate: true, shouldDirty: true });
            methods.setValue('is_information', false, { shouldValidate: true, shouldDirty: true });
          },
          onUncheck: () => {
            methods.setValue('is_top_display', false, { shouldValidate: true, shouldDirty: true });
          },
          checked: isTopDisplay,
        }}
        fieldRef={(el) => {
          if (el) {
            fieldRefs.current.is_top_display = el;
          }
        }}
      />

      <FormField
        control={methods.control}
        errors={serverErrors.is_information}
        label='お知らせ'
        labelPosition='left'
        name='is_information'
        component={Switch}
        componentProps={{
          onCheck: () => {
            methods.setValue('is_information', true, { shouldValidate: true, shouldDirty: true });
            methods.setValue('is_pickup', false, { shouldValidate: true, shouldDirty: true });
            methods.setValue('is_top_display', false, { shouldValidate: true, shouldDirty: true });
          },
          onUncheck: () => {
            methods.setValue('is_information', false, { shouldValidate: true, shouldDirty: true });
          },
          checked: isInformation,
        }}
        fieldRef={(el) => {
          if (el) {
            fieldRefs.current.is_information = el;
          }
        }}
      />

      <ImageUploadField
        onUpload={handleUpload}
        onDelete={handleDelete}
        preview={previewBase64 || methods.getValues('thumbnail_url')}
        isUploading={isUploading}
        errors={serverErrors.thumbnail_url}
      />

      <PDFUploadField
        onFileUpload={handlePdfUpload}
        onDelete={handlePdfDelete}
        preview={pdfUrl}
        originalFileName={originalPdfName}
        isUploading={isPdfUploading}
        maxFileSize={25 * 1024 * 1024}
        errors={serverErrors.pdf_url}
      />
    </>
  );
};

export default SettingForm;
